import { GLOBAL } from 'saddlebag-browser';

const $window = GLOBAL.getWindow();

type MaybeString = string | null | undefined;

export const isCrossDomainError = (message: unknown): boolean =>
  String(message ?? '').includes('Script error');

export const isInternalSourceError = (source: MaybeString): boolean => {
  if (!source) {
    return false;
  }

  const internalSources = [
    'skyscanner',
    'js.skyscnr.com',
    'css.skyscnr.com',
    'images.skyscnr.com',
    'tianxun',
  ];

  const errorDenyList = ['siteintercept.qualtrics'];

  const isSourceAllow = internalSources.some((allowedEntry) =>
    source.includes(allowedEntry),
  );

  if (!isSourceAllow) {
    return false;
  }

  const isSourceDeny = errorDenyList.some((denyEntry) =>
    source.includes(denyEntry),
  );

  return !isSourceDeny;
};

export const isInternalError = ({
  message,
  sourceDomain,
}: {
  message: unknown;
  sourceDomain: MaybeString;
}): boolean =>
  isInternalSourceError(sourceDomain) && !isCrossDomainError(message);

/*
  The last frame of the stack trace _should_ be where the error originated from. However New Relic sometimes adds a `nrWrapper` frame containing a reference to the page URL, which we want to ignore.

  This should have been fixed in v1222 of the New Relic browser agent, but still persists.

  A support case has been raised: https://support.newrelic.com/s/case/5008W00001BDsP5
*/

export const getSourceDomain = (stackTrace: string): string | undefined => {
  const allFrames = stackTrace.split('\n');

  const lastFrame = allFrames.at(-1);
  const secondLastFrame = allFrames.at(-2);
  const stackInitiatorFrame = lastFrame?.includes('nrWrapper')
    ? secondLastFrame
    : lastFrame;

  // The first URL reference within that frame _should_ be the source URL of the error. We take that risk trade off.
  const regex = /https?:\/\/([A-Za-z0-9.-]+)/;
  const match = regex.exec(stackInitiatorFrame ?? '');
  const sourceDomain = match?.[1];

  return sourceDomain;
};

type UnhandledDetail = {
  component: string;
  message: string;
  source?: string;
  stack?: string;
  [key: string]: unknown;
};

/*
  We only do this for unactionable unhandled errors that we do wish to keep daily visibility of for safety, but do not wish to pollute our error or warning logs with. This util is only intended for this file.
*/
export const logUnhandled = (fields: UnhandledDetail): void => {
  $window.performance?.mark?.('unhandled', { detail: fields });
};
